import useSWR from "swr";
import { Paginated, PaginationQuery } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { FastenerLight } from "./types";

/**
 * Geometry/role inputs backing the same fastener-compatibility rules the
 * backend already applies during configuration generation
 * (isFastenerCompatibleWithLock/Header/Joist/Inclined) — passed here so the
 * fastener select can be narrowed to compatible items too, without
 * duplicating those rules on the frontend.
 */
export type FastenerCompatibilityFilter = {
  /** "header", "joist", or "inclined" (header rule plus the inclined-specific length limit). */
  role: "header" | "joist" | "inclined";
  lockItemId?: string | null;
  secondLockItemId?: string | null;
  /** Used by the "header"/"inclined" roles: orientation picks headerH (column) vs headerB (beam), and
   * lateralResistanceConfig determines whether a header notch subtracts the lock's profile thickness. */
  headerB?: number | null;
  headerH?: number | null;
  headerOrientation?: string | null;
  lateralResistanceConfig?: number | null;
  alphaDeg?: number | null;
  betaDeg?: number | null;
  /** Structure finish ("ALUMINIUM"/"EVO") — one value for both connector pairs. */
  finish?: string | null;
};

export const useFasteners = (
  query: PaginationQuery,
  signal: AbortSignal,
  isAdmin?: boolean,
  compatibility?: FastenerCompatibilityFilter,
) => {
  const api = useApi();
  const prefix = isAdmin ? "/admin" : "";
  let url = buildUrl(`${prefix}/modules/lock_calculator/selects/fasteners`, query);
  if (compatibility) {
    const extraParams: Record<string, string> = {};
    for (const [key, value] of Object.entries(compatibility)) {
      if (value !== null && value !== undefined) extraParams[key] = String(value);
    }
    const search = new URLSearchParams(extraParams).toString();
    if (search) url += (url.includes("?") ? "&" : "?") + search;
  }
  return useSWR<Paginated<FastenerLight>, CustomError, string>(url, (requestUrl) =>
    api.get<Paginated<FastenerLight>>({ url: requestUrl, signal }),
  );
};
